import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  ChevronRight,
  Description,
  Download,
  DriveFileRenameOutline,
  ExpandMore,
  Folder,
  MoreVert,
  DeleteOutline,
  Settings,
  UploadFile,
} from '@mui/icons-material';
import { FileTreeFolderNode, FileTreeNode } from '../types';
import { defaultExpandedFolders, fileKindLabelKey, isFileTreeFolder } from './fileTree';
import { useI18n } from '../i18n';

interface ConfigFileTreeProps {
  tree: FileTreeFolderNode | null;
  selectedPath: string;
  disabled?: boolean;
  onSelect: (path: string) => void;
  onCreate: (prefix: 'layout' | 'flow') => void;
  onDownload: (path: string) => void;
  onRename: (from: string, to: string) => Promise<void> | void;
  onDelete: (path: string) => Promise<void> | void;
  onUpload: (file: File) => Promise<void> | void;
}

function TreeNode({
  node,
  depth,
  selectedPath,
  disabled,
  expanded,
  onToggle,
  onSelect,
  onMenuOpen,
  renaming,
  renameValue,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
}: {
  node: FileTreeNode;
  depth: number;
  selectedPath: string;
  disabled?: boolean;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
  onMenuOpen: (path: string, anchor: HTMLElement) => void;
  renaming: string | null;
  renameValue: string;
  onRenameChange: (value: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
}) {
  const { t } = useI18n();

  if (node.kind === 'file') {
    if (renaming === node.path) {
      return (
        <Box px={0.5} py={0.5} pl={depth * 2 + 0.5}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onBlur={onRenameCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRenameCommit();
              if (e.key === 'Escape') onRenameCancel();
            }}
          />
        </Box>
      );
    }

    const icon = node.fileKind === 'settings'
      ? <Settings fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
      : node.fileKind === 'flow'
        ? <AccountTree fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
        : <Description fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />;

    return (
      <ListItemButton
        selected={selectedPath === node.path}
        onClick={() => onSelect(node.path)}
        disabled={disabled}
        sx={{ py: 0.25, borderRadius: 1, pr: 0.5, pl: depth * 2 + 1 }}
      >
        {icon}
        <ListItemText
          primary={node.name}
          secondary={node.fileKind !== 'file' ? t(fileKindLabelKey(node.fileKind)) : undefined}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
        />
        {node.missing && <Chip size="small" color="warning" label={t('common.new')} />}
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onMenuOpen(node.path, e.currentTarget);
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </ListItemButton>
    );
  }

  const isOpen = expanded.has(node.path);

  return (
    <>
      <ListItemButton
        onClick={() => onToggle(node.path)}
        sx={{ py: 0.25, borderRadius: 1, pl: depth * 2 + 1 }}
        disabled={disabled}
      >
        {isOpen ? <ExpandMore fontSize="small" sx={{ mr: 0.5 }} /> : <ChevronRight fontSize="small" sx={{ mr: 0.5 }} />}
        <Folder fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
        <ListItemText
          primary={node.name || t('files.treeRoot')}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {node.children.map((child) => (
            <TreeNode
              key={child.kind === 'folder' ? child.path : child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              disabled={disabled}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              onMenuOpen={onMenuOpen}
              renaming={renaming}
              renameValue={renameValue}
              onRenameChange={onRenameChange}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

const ConfigFileTree: React.FC<ConfigFileTreeProps> = ({
  tree,
  selectedPath,
  disabled,
  onSelect,
  onCreate,
  onDownload,
  onRename,
  onDelete,
  onUpload,
}) => {
  const { t } = useI18n();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [menuPath, setMenuPath] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['/']));

  const defaultExpanded = useMemo(() => (tree ? defaultExpandedFolders(tree) : ['/']), [tree]);

  React.useEffect(() => {
    setExpanded(new Set(defaultExpanded));
  }, [defaultExpanded]);

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuPath(null);
  };

  const startRename = (path: string) => {
    setRenaming(path);
    setRenameValue(path.replace(/^\//, ''));
    closeMenu();
  };

  const commitRename = async () => {
    if (!renaming) return;
    const next = renameValue.trim().replace(/\\/g, '/');
    const to = next.startsWith('/') ? next : `/${next}`;
    if (to !== renaming) {
      await onRename(renaming, to);
    }
    setRenaming(null);
  };

  const toggleFolder = (folderPath: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });
  };

  const rootChildren = tree?.children || [];
  const hasFiles = rootChildren.length > 0;

  return (
    <Box px={1} pt={1} pb={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="subtitle2">{t('files.explorer')}</Typography>
        <IconButton size="small" disabled={disabled} onClick={() => uploadRef.current?.click()} title={t('files.uploadFile')}>
          <UploadFile fontSize="small" />
        </IconButton>
        <input
          ref={uploadRef}
          type="file"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />
      </Box>
      <List dense disablePadding sx={{ mb: 1, maxHeight: 320, overflow: 'auto' }}>
        {!hasFiles ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            {t('files.explorerEmpty')}
          </Typography>
        ) : (
          rootChildren.map((child) => (
            <TreeNode
              key={isFileTreeFolder(child) ? child.path : child.path}
              node={child}
              depth={0}
              selectedPath={selectedPath}
              disabled={disabled}
              expanded={expanded}
              onToggle={toggleFolder}
              onSelect={onSelect}
              onMenuOpen={(path, anchor) => {
                setAnchorEl(anchor);
                setMenuPath(path);
              }}
              renaming={renaming}
              renameValue={renameValue}
              onRenameChange={setRenameValue}
              onRenameCommit={commitRename}
              onRenameCancel={() => setRenaming(null)}
            />
          ))
        )}
      </List>
      <Box display="flex" gap={0.5} flexWrap="wrap" px={0.5}>
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => onCreate('layout')}>
          {t('files.newLayout')}
        </Button>
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => onCreate('flow')}>
          {t('files.newFlow')}
        </Button>
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuPath) onDownload(menuPath);
            closeMenu();
          }}
        >
          <Download fontSize="small" sx={{ mr: 1 }} /> {t('common.download')}
        </MenuItem>
        <MenuItem
          disabled={menuPath === '/settings.json'}
          onClick={() => menuPath && startRename(menuPath)}
        >
          <DriveFileRenameOutline fontSize="small" sx={{ mr: 1 }} /> {t('files.rename')}
        </MenuItem>
        <MenuItem
          disabled={menuPath === '/settings.json'}
          onClick={async () => {
            if (menuPath && window.confirm(t('files.deleteConfirm', { path: menuPath }))) {
              await onDelete(menuPath);
            }
            closeMenu();
          }}
        >
          <DeleteOutline fontSize="small" sx={{ mr: 1 }} /> {t('common.delete')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ConfigFileTree;
